import {
  DefaultTransformer,
  deserialize,
  DocumentObject,
  JsonObject,
  transform,
  Transformer,
} from '@jsonapiworld/jsonapi-serializer';
import { BigNumber } from 'bignumber.js';
import { addDays, parseISO } from 'date-fns';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import {
  AdjustmentPayloadWithLid,
  CreateBookkeepingAdjustmentTransformer,
} from '~/api/bookkeeping/bookkeeping-transactions';
import { VATCodeResourceType } from '~/api/bookkeeping/vat-codes';
import { backendApiGateway } from '~/client/backend';
import { CompanyResourceType } from '~/types/company';
import { CounterpartResourceType } from '~/types/counterparts';
import { Document, DocumentResourceType } from '~/types/document';
import { FixedAssetPayload } from '~/types/fixed-asset';
import {
  BillsToRegisterResponse,
  PreviewVATRegistrationNumberRequest,
  PreviewVATRegistrationNumberResponse,
  VATRegistrationBillsPayload,
  VATRegistrationBillsVATCodePayload,
  VATRegistrationBulkRegisterRequest,
  VATRegistrationBulkRegisterResponse,
  CreateBookkeepingEntry,
  CreateBookkeepingTransaction,
  UpdateBookkeepingTransactionForVAT,
} from '~/types/general-bookkeeping';
import { MetaDataCursorPage } from '~/types/jsonapi';
import { isDefined } from '~/types/typeGuards';
import {
  CreateVATEntry,
  CreateVATRegistration,
  FetchBillsToRegisterFilters,
  UpdateVATEntry,
  UpdateVATRegistration,
  VATBook,
  VATRegistrationEntry,
  VATRegistration,
  VATRegistrationsFilters,
  BillTypeFilter,
} from '~/types/vat-bookkeeping';
import { formatDateForAPI, formatISOForAPI } from '~/utils/formatDate';
import {
  buildJsonAPIQueryParams,
  jsonAPICursorPaginatorBuilder,
  jsonAPIFieldsBuilder,
  jsonAPIFilterBuilder,
  jsonAPIIncludeBuilder,
  jsonAPISortBuilder,
} from '~/utils/jsonapi-query';

export const registrationsPageSize = 50;

const parseDateOrNull = (value: Date | string | null | undefined) => {
  if (!value) {
    return null;
  }

  return value instanceof Date ? value : parseISO(value);
};

const normalizeVATEntryDates = (entry: VATRegistrationEntry): VATRegistrationEntry => ({
  ...entry,
  purchasePeriodStart: parseDateOrNull(entry.purchasePeriodStart),
  purchasePeriodEnd: parseDateOrNull(entry.purchasePeriodEnd),
});

const normalizeVATRegistrationDates = (registration: VATRegistration): VATRegistration => ({
  ...registration,
  vatEntries: registration.vatEntries.map(normalizeVATEntryDates),
});

const deserializeVATRegistrations = (payload: DocumentObject): VATRegistration[] => {
  const deserialized = deserialize<VATRegistration>(payload) as VATRegistration[];
  return deserialized.map(normalizeVATRegistrationDates);
};

interface FetchRegistrationsOptions {
  size?: number;
  cursor?: string;
}

export const fetchVATRegistrations = async (
  companyId: string,
  filters: VATRegistrationsFilters,
  options?: FetchRegistrationsOptions,
) => {
  const filtersBuilder = jsonAPIFilterBuilder();
  filtersBuilder.eq('company.id', companyId);
  if (filters.vatBookId) {
    filtersBuilder.eq('vatBook.id', filters.vatBookId);
  }
  if (filters.search) {
    filtersBuilder.ilike('search', filters.search);
  }
  if (filters.documentId) {
    filtersBuilder.eq('documentId', filters.documentId);
  }
  const includeBuilder = jsonAPIIncludeBuilder([
    'documents',
    'vatEntries',
    'vatEntries.bookkeepingAccount',
    'vatEntries.vatCode',
    'vatEntries.fixedAssets',
    'vatEntries.fixedAssets.speciesCategory',
    'vatEntries.bookkeepingAdjustment',
    'vatEntries.bookkeepingAdjustment.bookkeepingAccount',
    'vatBook',
    'vatReportingPeriod',
    'bookkeepingTransaction',
    'bookkeepingTransaction.bookkeepingEntries',
    'bookkeepingTransaction.bookkeepingEntries.bookkeepingAccount',
    'bookkeepingTransaction.bookkeepingEntries.counterpart',
    'vatRegistrationBills',
    'vatRegistrationBills.cashBookkeepingAccount',
    'vatRegistrationBills.electronicCashBookkeepingAccount',
    'vatRegistrationBills.ticketCashBookkeepingAccount',
    'vatRegistrationBills.ticketRevenueBookkeepingAccount',
    'vatRegistrationBills.vatCodes',
    'vatRegistrationBills.vatCodes.vatCode',
    'vatRegistrationBills.vatCodes.bookkeepingAccount',
  ]);
  const sortBuilder = jsonAPISortBuilder([
    { sort: 'date', direction: 'asc' },
    { sort: 'number', direction: 'asc' },
  ]);

  const paginationParamsBuilder = jsonAPICursorPaginatorBuilder();
  paginationParamsBuilder.size(options?.size ?? registrationsPageSize);
  if (options?.cursor) paginationParamsBuilder.cursor(options.cursor);

  const { data: results } = await backendApiGateway.get<DocumentObject & { meta: { page: MetaDataCursorPage } }>(
    '/api/v1/vat-registrations',
    {
      params: buildJsonAPIQueryParams(filtersBuilder, includeBuilder, paginationParamsBuilder, sortBuilder),
    },
  );
  return {
    registrations: deserializeVATRegistrations(results),
    page: (results.meta as { page: MetaDataCursorPage }).page,
  };
};

export const fetchVATRegistration = async (registrationId: VATRegistration['id']) => {
  const includeBuilder = jsonAPIIncludeBuilder([
    'documents',
    'counterpart',
    'vatEntries',
    'vatEntries.bookkeepingAccount',
    'vatEntries.vatCode',
    'vatEntries.fixedAssets',
    'vatEntries.fixedAssets.speciesCategory',
    'vatEntries.bookkeepingAdjustment',
    'vatEntries.bookkeepingAdjustment.bookkeepingAccount',
    'vatBook',
    'vatReportingPeriod',
    'bookkeepingTransaction',
    'bookkeepingTransaction.bookkeepingEntries',
    'bookkeepingTransaction.bookkeepingEntries.bookkeepingAccount',
    'bookkeepingTransaction.bookkeepingEntries.counterpart',
    'vatRegistrationBills',
    'vatRegistrationBills.cashBookkeepingAccount',
    'vatRegistrationBills.electronicCashBookkeepingAccount',
    'vatRegistrationBills.ticketCashBookkeepingAccount',
    'vatRegistrationBills.ticketRevenueBookkeepingAccount',
    'vatRegistrationBills.vatCodes',
    'vatRegistrationBills.vatCodes.vatCode',
    'vatRegistrationBills.vatCodes.bookkeepingAccount',
  ]);

  const { data: result } = await backendApiGateway.get<DocumentObject>(`/api/v1/vat-registrations/${registrationId}`, {
    params: buildJsonAPIQueryParams(includeBuilder),
  });
  const deserialized = deserialize<VATRegistration>(result) as VATRegistration;
  return normalizeVATRegistrationDates(deserialized);
};

export async function fetchVATRegistrationNumber(companyId: string, vatBookId: VATBook['id'] | null) {
  if (!vatBookId) {
    return 1;
  }
  const filtersBuilder = jsonAPIFilterBuilder();
  filtersBuilder.eq('company.id', companyId);
  filtersBuilder.eq('vatBook.id', vatBookId);
  const fieldsBuilder = jsonAPIFieldsBuilder([{ resource: 'vat-registration', fields: ['number'] }]);
  const sortBuilder = jsonAPISortBuilder([{ sort: 'number', direction: 'desc' }]);

  const queryParams = buildJsonAPIQueryParams(filtersBuilder, fieldsBuilder, sortBuilder);

  const { data: results } = await backendApiGateway.get<DocumentObject>('/api/v1/vat-registrations', {
    params: queryParams,
  });
  const registrations = deserialize<VATRegistration>(results) as VATRegistration[];
  return (registrations[0]?.number ?? 0) + 1;
}

class FixedAssetTransformer extends Transformer<FixedAssetPayload & { lid: string }, {}> {
  constructor() {
    super();
    this.type = 'fixed-asset';
    this.relationships = {
      speciesCategory: ({ speciesCategoryId }) =>
        transform()
          .withInput(speciesCategoryId ? { id: speciesCategoryId } : [])
          .withTransformer(new DefaultTransformer('fixed-asset-species-category'))
          .toContext(),
    };
  }

  transform(payload: FixedAssetPayload & { lid: string }): JsonObject {
    const { lid, speciesCategoryId: _speciesCategoryId, ...attributes } = payload;
    return { lid, ...attributes };
  }
}

const fixedAssetsRelationship = (entryLid: string, fixedAssets?: FixedAssetPayload[]) => {
  const assets = (fixedAssets ?? []).map((asset, index) => ({ ...asset, lid: `fixed-asset-${entryLid}-${index}` }));
  return transform<FixedAssetPayload & { lid: string }, {}>()
    .withInput(assets)
    .withTransformer(new FixedAssetTransformer())
    .withIncluded(true)
    .toContext();
};

class CreateVATEntryTransformer extends Transformer<CreateVATEntry & { lid: string }, {}> {
  constructor() {
    super();
    this.type = 'vat-entry';
    this.relationships = {
      vatCode: ({ vatCodeId }) =>
        transform()
          .withInput({ id: vatCodeId })
          .withTransformer(new DefaultTransformer(VATCodeResourceType))
          .toContext(),
      bookkeepingAccount: ({ bookkeepingAccountId }) =>
        transform()
          .withInput({ id: bookkeepingAccountId })
          .withTransformer(new DefaultTransformer('bookkeeping-account'))
          .toContext(),
      fixedAssets: ({ lid, fixedAssets }) => fixedAssetsRelationship(lid, fixedAssets),
      bookkeepingAdjustment: ({ bookkeepingAdjustment, lid }) => {
        if (!bookkeepingAdjustment) {
          return transform()
            .withInput(null)
            .withTransformer(new DefaultTransformer('bookkeeping-adjustment'))
            .toContext();
        }
        return transform<AdjustmentPayloadWithLid, {}>()
          .withInput({ ...bookkeepingAdjustment, lid: `adj-${lid}` })
          .withTransformer(new CreateBookkeepingAdjustmentTransformer())
          .withIncluded(true)
          .toContext();
      },
    };
  }

  transform(payload: CreateVATEntry & { lid: string }): JsonObject {
    return {
      lid: payload.lid,
      taxableAmount: { amount: payload.taxableAmount.amount, currency: payload.taxableAmount.currency },
      vatPercentage: BigNumber(payload.vatPercentage).decimalPlaces(0).toNumber(),
      taxAmount: { amount: payload.taxAmount.amount, currency: payload.taxAmount.currency },
      source: payload.source,
      proposal: payload.proposal,
      documentLineItems: payload.documentLineItems,
      purchaseType: payload.purchaseType,
      purchasePeriodStart: payload.purchasePeriodStart ? formatDateForAPI(payload.purchasePeriodStart) : null,
      purchasePeriodEnd: payload.purchasePeriodEnd ? formatDateForAPI(payload.purchasePeriodEnd) : null,
    };
  }
}

class CreateBookkeepingEntryForVATTransformer extends Transformer<CreateBookkeepingEntry, {}> {
  constructor() {
    super();
    this.type = 'bookkeeping-entry';
    this.relationships = {
      bookkeepingAccount: ({ bookkeepingAccountId }) =>
        transform()
          .withInput({ id: bookkeepingAccountId })
          .withTransformer(new DefaultTransformer('bookkeeping-account'))
          .toContext(),
      document: ({ documentId }) =>
        transform()
          .withInput(documentId ? { id: documentId } : null)
          .withTransformer(new DefaultTransformer(DocumentResourceType))
          .toContext(),
      counterpart: ({ counterpartId }) =>
        transform()
          .withInput(counterpartId ? { id: counterpartId } : null)
          .withTransformer(new DefaultTransformer(CounterpartResourceType))
          .toContext(),
    };
  }

  transform(payload: CreateBookkeepingEntry): JsonObject {
    return {
      lid: payload.lid,
      amount: payload.amount,
      source: payload.source,
    };
  }
}

class CreateBookkeepingTransactionForVATTransformer extends Transformer<
  (CreateBookkeepingTransaction & { lid: string }) | null,
  {}
> {
  constructor() {
    super();
    this.type = 'bookkeeping-transaction';

    this.relationships = {
      company: (transaction) => {
        if (!transaction) {
          return transform().withInput([]).withTransformer(new DefaultTransformer(CompanyResourceType)).toContext();
        }

        return transform()
          .withInput({ id: transaction.companyId })
          .withTransformer(new DefaultTransformer(CompanyResourceType))
          .toContext();
      },

      bookkeepingEntries: (transaction) => {
        const entries: CreateBookkeepingEntry[] =
          transaction?.bookkeepingEntries.map((entry, index) => ({
            lid: index.toString(),
            bookkeepingAccountId: entry.bookkeepingAccountId,
            amount: entry.amount,
            counterpartId: entry.counterpartId,
            documentId: entry.documentId,
            source: entry.source,
          })) ?? [];

        return transform<CreateBookkeepingEntry, {}>()
          .withInput(entries)
          .withTransformer(new CreateBookkeepingEntryForVATTransformer())
          .withIncluded(true)
          .toContext();
      },
    };
  }

  transform(transaction: (CreateBookkeepingTransaction & { lid: string }) | null): {} {
    if (!transaction) {
      return {};
    }

    const { lid, date, description, transactionIds, recommendedReconciliationFlowId } = transaction;

    return {
      lid,
      date: formatDateForAPI(date),
      description,
      transactionId: transactionIds[0],
      recommendedReconciliationFlowId,
    };
  }
}

class UpdateBookkeepingTransactionForVATTransformer extends Transformer<
  UpdateBookkeepingTransactionForVAT | undefined,
  {}
> {
  constructor() {
    super();
    this.type = 'bookkeeping-transaction';
    this.relationships = {
      bookkeepingEntries: (data) =>
        transform<CreateBookkeepingEntry & { lid?: string }, {}>()
          .withInput(
            data?.bookkeepingEntries.map((entry, index) => ({
              ...entry,
              lid: index.toString(),
            })) ?? [],
          )
          .withTransformer(new CreateBookkeepingEntryForVATTransformer())
          .withIncluded(true)
          .toContext(),
    };
  }

  transform(payload: UpdateBookkeepingTransactionForVAT | undefined): JsonObject {
    return payload?.description ? { description: payload.description } : {};
  }
}

class CreateVATRegistrationTransformer extends Transformer<CreateVATRegistration, {}> {
  constructor() {
    super();
    this.type = 'vat-registration';
    this.relationships = {
      company: ({ companyId }) =>
        transform()
          .withInput({ id: companyId })
          .withTransformer(new DefaultTransformer(CompanyResourceType))
          .toContext(),
      documents: ({ documentIds }) =>
        transform()
          .withInput(documentIds.map((id) => ({ id })))
          .withTransformer(new DefaultTransformer(DocumentResourceType))
          .toContext(),
      vatBook: ({ vatBookId }) =>
        transform().withInput({ id: vatBookId }).withTransformer(new DefaultTransformer('vat-book')).toContext(),
      vatEntries: ({ entries }) =>
        transform<CreateVATEntry & { lid: string }, {}>()
          .withInput(entries.map((entry, index) => ({ ...entry, lid: index.toString() })))
          .withTransformer(new CreateVATEntryTransformer())
          .withIncluded(true)
          .toContext(),
      vatReportingPeriod: ({ reportingPeriodId }) =>
        transform()
          .withInput({ id: reportingPeriodId })
          .withTransformer(new DefaultTransformer('vat-reporting-period'))
          .toContext(),
      counterpart: ({ counterpartId }) =>
        transform()
          .withInput(counterpartId ? { id: counterpartId } : null)
          .withTransformer(new DefaultTransformer(CounterpartResourceType))
          .toContext(),
      bookkeepingTransaction: ({ bookkeepingTransaction, companyId }) =>
        bookkeepingTransaction
          ? transform<CreateBookkeepingTransaction & { lid: string }, {}>()
              .withInput({
                ...bookkeepingTransaction,
                companyId,
                lid: 'bookkeeping-transaction',
              })
              .withTransformer(new CreateBookkeepingTransactionForVATTransformer())
              .withIncluded(true)
              .toContext()
          : transform<(CreateBookkeepingTransaction & { lid: string }) | null, {}>()
              .withInput(null)
              .withTransformer(new CreateBookkeepingTransactionForVATTransformer())
              .withIncluded(true)
              .toContext(),
    };
  }

  transform(payload: CreateVATRegistration): JsonObject {
    return { date: formatDateForAPI(payload.date), number: payload.number };
  }
}

export const createVATRegistration = async (values: CreateVATRegistration): Promise<VATRegistration> => {
  const payload = transform<CreateVATRegistration, {}>()
    .withInput(values)
    .withTransformer(new CreateVATRegistrationTransformer())
    .serialize();

  const { data: result } = await backendApiGateway.post<DocumentObject>('/api/v1/vat-registrations', payload);
  return deserialize<VATRegistration>(result) as VATRegistration;
};

class UpdateVATRegistrationBillsVATCodeTransformer extends Transformer<
  VATRegistrationBillsVATCodePayload & { lid: string },
  {}
> {
  constructor() {
    super();
    this.type = 'vat-registration-bills-vat-code';
    this.relationships = {
      vatCode: ({ vatCode }) =>
        transform().withInput(vatCode).withTransformer(new DefaultTransformer(VATCodeResourceType)).toContext(),
      bookkeepingAccount: ({ bookkeepingAccount }) =>
        transform()
          .withInput(bookkeepingAccount)
          .withTransformer(new DefaultTransformer('bookkeeping-account'))
          .toContext(),
    };
  }

  transform(payload: VATRegistrationBillsVATCodePayload & { lid: string }): JsonObject {
    return { lid: payload.lid };
  }
}

class UpdateVATRegistrationBillsTransformer extends Transformer<
  (VATRegistrationBillsPayload & { lid: string }) | null,
  {}
> {
  constructor() {
    super();
    this.type = 'vat-registration-bills';
    this.relationships = {
      cashBookkeepingAccount: (payload) =>
        transform()
          .withInput(payload?.cashBookkeepingAccount ?? null)
          .withTransformer(new DefaultTransformer('bookkeeping-account'))
          .toContext(),
      electronicCashBookkeepingAccount: (payload) =>
        transform()
          .withInput(payload?.electronicCashBookkeepingAccount ?? null)
          .withTransformer(new DefaultTransformer('bookkeeping-account'))
          .toContext(),
      ticketCashBookkeepingAccount: (payload) =>
        transform()
          .withInput(payload?.ticketCashBookkeepingAccount ?? null)
          .withTransformer(new DefaultTransformer('bookkeeping-account'))
          .toContext(),
      ticketRevenueBookkeepingAccount: (payload) =>
        transform()
          .withInput(payload?.ticketRevenueBookkeepingAccount ?? null)
          .withTransformer(new DefaultTransformer('bookkeeping-account'))
          .toContext(),
      vatCodes: (payload) =>
        transform<VATRegistrationBillsVATCodePayload & { lid: string }, {}>()
          .withInput(
            payload?.vatCodes.map((row, index) => ({ ...row, lid: `vat-registration-bills-vat-code-${index}` })) ?? [],
          )
          .withTransformer(new UpdateVATRegistrationBillsVATCodeTransformer())
          .withIncluded(true)
          .toContext(),
    };
  }

  transform(payload: (VATRegistrationBillsPayload & { lid: string }) | null): JsonObject {
    return payload ? { lid: payload.lid } : {};
  }
}

class UpdateVATEntryTransformer extends Transformer<UpdateVATEntry & { lid?: string }, {}> {
  constructor() {
    super();
    this.type = 'vat-entry';
    this.relationships = {
      vatCode: ({ vatCodeId }) =>
        transform()
          .withInput({ id: vatCodeId })
          .withTransformer(new DefaultTransformer(VATCodeResourceType))
          .toContext(),
      bookkeepingAccount: ({ bookkeepingAccountId }) =>
        transform()
          .withInput({ id: bookkeepingAccountId })
          .withTransformer(new DefaultTransformer('bookkeeping-account'))
          .toContext(),
      fixedAssets: ({ lid, id, fixedAssets }) => fixedAssetsRelationship(id ?? lid ?? 'entry', fixedAssets),
      bookkeepingAdjustment: ({ bookkeepingAdjustment, id, lid }) => {
        if (!bookkeepingAdjustment) {
          return transform()
            .withInput(null)
            .withTransformer(new DefaultTransformer('bookkeeping-adjustment'))
            .toContext();
        }
        return transform<AdjustmentPayloadWithLid, {}>()
          .withInput({ ...bookkeepingAdjustment, lid: `adj-${id ?? lid ?? 'entry'}` })
          .withTransformer(new CreateBookkeepingAdjustmentTransformer())
          .withIncluded(true)
          .toContext();
      },
    };
  }

  transform(payload: UpdateVATEntry & { lid?: string }): JsonObject {
    return {
      taxableAmount: payload.taxableAmount,
      lid: payload.lid,
      source: payload.source,
      vatPercentage: BigNumber(payload.vatPercentage).decimalPlaces(0).toNumber(),
      taxAmount: { amount: payload.taxAmount.amount, currency: payload.taxAmount.currency },
      purchaseType: payload.purchaseType,
      purchasePeriodStart: payload.purchasePeriodStart ? formatDateForAPI(payload.purchasePeriodStart) : null,
      purchasePeriodEnd: payload.purchasePeriodEnd ? formatDateForAPI(payload.purchasePeriodEnd) : null,
    };
  }
}

class UpdateVATRegistrationTransformer extends Transformer<UpdateVATRegistration, {}> {
  constructor() {
    super();
    this.type = 'vat-registration';
    this.relationships = {
      vatBook: ({ vatBookId }) =>
        transform().withInput({ id: vatBookId }).withTransformer(new DefaultTransformer('vat-book')).toContext(),
      vatEntries: ({ entries }) =>
        transform<UpdateVATEntry & { lid?: string }, {}>()
          .withInput(entries.map((entry, index) => ({ ...entry, lid: entry.id ? undefined : index.toString() })))
          .withTransformer(new UpdateVATEntryTransformer())
          .withIncluded(true)
          .toContext(),
      vatReportingPeriod: ({ reportingPeriodId }) =>
        transform()
          .withInput({ id: reportingPeriodId })
          .withTransformer(new DefaultTransformer('vat-reporting-period'))
          .toContext(),
      counterpart: ({ counterpartId }) =>
        transform()
          .withInput(counterpartId ? { id: counterpartId } : null)
          .withTransformer(new DefaultTransformer(CounterpartResourceType))
          .toContext(),
      bookkeepingTransaction: ({ bookkeepingTransaction }) =>
        transform<UpdateBookkeepingTransactionForVAT | undefined, {}>()
          .withInput(bookkeepingTransaction)
          .withTransformer(new UpdateBookkeepingTransactionForVATTransformer())
          .withIncluded(true)
          .toContext(),
      vatRegistrationBills: ({ vatRegistrationBills }) =>
        vatRegistrationBills
          ? transform<VATRegistrationBillsPayload & { lid: string }, {}>()
              .withInput({ ...vatRegistrationBills, lid: 'vat-registration-bills' })
              .withTransformer(new UpdateVATRegistrationBillsTransformer())
              .withIncluded(true)
              .toContext()
          : transform<VATRegistrationBillsPayload & { lid: string }, {}>()
              .withTransformer(new UpdateVATRegistrationBillsTransformer())
              .withIncluded(true)
              .toContext(),
    };
  }

  transform(payload: UpdateVATRegistration): JsonObject {
    return { date: formatDateForAPI(payload.date) };
  }
}

export const updateVATRegistration = async (id: VATRegistration['id'], values: UpdateVATRegistration) => {
  const payload = transform<UpdateVATRegistration, {}>()
    .withInput(values)
    .withTransformer(new UpdateVATRegistrationTransformer())
    .serialize();

  const { data: result } = await backendApiGateway.patch<DocumentObject>(`/api/v1/vat-registrations/${id}`, payload);
  return deserialize<VATRegistration>(result) as VATRegistration;
};

export const deleteVATRegistrationsByDocumentId = async (documentId: Document['id']) => {
  await backendApiGateway.delete<DocumentObject>(`/api/v1/vat-registrations/document/${documentId}`);
};

export const deleteVATRegistrationById = async (id: VATRegistration['id']) => {
  await backendApiGateway.delete<DocumentObject>(`/api/v1/vat-registrations/${id}`);
};

export const fetchBillsToRegister = async (
  companyId: string,
  filters: FetchBillsToRegisterFilters,
): Promise<BillsToRegisterResponse> => {
  const filtersBuilder = jsonAPIFilterBuilder();
  filtersBuilder.eq('company.id', companyId);

  if (!isNil(filters.creationDate?.[0])) {
    const creationDate = formatISOForAPI(filters.creationDate[0]);
    filtersBuilder.gte('creationDate', creationDate);
  }
  if (!isNil(filters.creationDate?.[1])) {
    const creationDate = formatDateForAPI(addDays(parseISO(filters.creationDate[1]), 1));
    filtersBuilder.lt('creationDate', creationDate);
  }
  if (isDefined(filters.deviceIds) && !isEmpty(filters.deviceIds)) {
    filtersBuilder.in('deviceId', filters.deviceIds);
  }
  if (isDefined(filters.billType)) {
    filtersBuilder.empty('deviceId', filters.billType === BillTypeFilter.Other);
  }

  const queryParams = buildJsonAPIQueryParams(filtersBuilder);
  const { data: result } = await backendApiGateway.get<BillsToRegisterResponse>(
    `/api/v1/vat-registrations/bills-to-register`,
    { params: queryParams },
  );
  return result;
};

export const bulkBookkeepingTransactionsRegistration = async (
  requestData: VATRegistrationBulkRegisterRequest,
): Promise<VATRegistrationBulkRegisterResponse> => {
  const { data: result } = await backendApiGateway.post<VATRegistrationBulkRegisterResponse>(
    `/api/v1/vat-registrations/bulk-register`,
    requestData,
    {
      headers: {
        'content-type': 'application/json',
      },
    },
  );
  return result;
};

export const previewVATRegistrationNumber = async (
  requestData: PreviewVATRegistrationNumberRequest,
): Promise<PreviewVATRegistrationNumberResponse> => {
  const { data: result } = await backendApiGateway.post<PreviewVATRegistrationNumberResponse>(
    `/api/v1/vat-registrations/number/preview`,
    requestData,
    {
      headers: {
        'content-type': 'application/json',
      },
    },
  );
  return result;
};
